/**
 * TCB R39 Markets / multi-currency / international B2B runtime.
 * Composes market readiness + package sellability. No theme FX checkout authority.
 */
(function (global) {
  'use strict';

  var CFG = global.TCB_MARKETS_INTELLIGENCE || null;
  var MAX_SKUS = 8;
  var READY = { READY_DIRECT: true, READY_B2B: true };

  function loadConfig(cb) {
    if (!CFG && global.TCB_MARKETS_INTELLIGENCE) CFG = global.TCB_MARKETS_INTELLIGENCE;
    if (CFG) {
      cb(null, CFG);
      return;
    }
    var node = document.querySelector('[data-tcb-markets-runtime-url]');
    var url = node && node.getAttribute('data-tcb-markets-runtime-url');
    if (!url || !global.fetch) {
      cb(new Error('markets_config_missing'));
      return;
    }
    global
      .fetch(url)
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        CFG = json;
        global.TCB_MARKETS_INTELLIGENCE = json;
        cb(null, json);
      })
      .catch(function (err) {
        cb(err || new Error('markets_fetch_failed'));
      });
  }

  function getMarket(marketKey) {
    return ((CFG && CFG.markets) || []).find(function (m) {
      return m.market_key === marketKey;
    }) || null;
  }

  function resolveBuyerContext(input) {
    input = input || {};
    var companyId = input.company_id || null;
    var locationId = input.company_location_id || input.location_id || null;
    var region = input.region || null;
    var marketKey = input.market_key || null;

    // Company-location outranks generic region
    if (locationId) {
      var locMarket = ((CFG && CFG.markets) || []).find(function (m) {
        return (m.company_location_scope || []).indexOf(locationId) !== -1;
      });
      if (locMarket) {
        return {
          market_key: locMarket.market_key,
          company_id: companyId,
          company_location_id: locationId,
          currency: locMarket.currency,
          precedence: 'company_location',
          stale_cleared: true
        };
      }
    }

    if (!marketKey && region) {
      var byRegion = ((CFG && CFG.markets) || []).find(function (m) {
        return m.region === region || (m.region && region && m.region.indexOf(region) === 0);
      });
      if (byRegion) marketKey = byRegion.market_key;
    }

    var market = getMarket(marketKey || 'us-consumer');
    return {
      market_key: market ? market.market_key : 'intl-unsupported',
      company_id: companyId,
      company_location_id: locationId,
      currency: market ? market.currency : null,
      precedence: locationId ? 'company_location_unmatched_fallback' : 'region_or_default',
      stale_cleared: true
    };
  }

  function evaluatePackageSellability(packageId, buyerContext, opts) {
    opts = opts || {};
    var ctx = resolveBuyerContext(buyerContext || {});
    var market = getMarket(ctx.market_key);
    var reasons = [];
    var decision = 'REVIEW_REQUIRED';
    var skuCount = opts.unique_sku_count || 0;

    if (!market) {
      return { decision: 'UNAVAILABLE', reasons: ['unknown_market'], buyer_context: ctx, ok: false };
    }
    if (skuCount > MAX_SKUS) {
      return { decision: 'UNAVAILABLE', reasons: ['sku_cap_exceeded'], buyer_context: ctx, ok: false, max_skus: MAX_SKUS };
    }

    var preset = ((CFG && CFG.package_market_decisions) || []).find(function (d) {
      return (
        d.package_id === packageId &&
        d.buyer_context &&
        d.buyer_context.market_key === ctx.market_key &&
        (d.buyer_context.company_location_id || null) === (ctx.company_location_id || null)
      );
    });
    if (preset) {
      decision = preset.decision;
      reasons = (preset.reasons || []).slice();
    } else if (market.state === 'TEMP_BLOCKED' || market.state === 'NOT_CONFIGURED' || market.state === 'RETIRED') {
      decision = 'UNAVAILABLE';
      reasons.push(market.block_reason || 'market_blocked');
    } else if (market.state === 'PILOT' || market.state === 'QUOTE_ONLY' || !market.gates || !market.gates.direct_buy_ready) {
      decision = 'QUOTE_ONLY';
      reasons.push(market.block_reason || 'market_not_ready_direct');
    } else if (READY[market.state]) {
      decision = 'DIRECT_BUY';
    }

    // Freight truth: unknown never free — large machines default review
    if (opts.machine_class === 'large' && opts.freight_confidence !== 'high') {
      if (decision === 'DIRECT_BUY') decision = 'QUOTE_ONLY';
      reasons.push('freight_review_required');
    }

    // R37: never copy US exemption across jurisdictions (hard override)
    if (opts.assume_us_resale_abroad) {
      decision = 'REVIEW_REQUIRED';
      reasons.push('cross_jurisdiction_tax_forbidden');
    }

    // Margin guard: localized price cannot activate below R33 (hard override)
    if (opts.below_r33_economics) {
      decision = 'REVIEW_REQUIRED';
      reasons.push('margin_guard_r33');
    }

    return {
      decision: decision,
      reasons: reasons,
      buyer_context: ctx,
      ok: decision === 'DIRECT_BUY',
      market_state: market.state,
      evidence_ref: preset ? preset.evidence_ref || null : null
    };
  }

  function isReplenishmentAllowed(marketKey, context) {
    var ctx = resolveBuyerContext(Object.assign({ market_key: marketKey }, context || {}));
    var market = getMarket(ctx.market_key);
    if (!market) return { ok: false, reason: 'unknown_market' };
    if (!market.gates || !market.gates.replenishment_ready) return { ok: false, reason: 'market_replenishment_not_ready', market_key: market.market_key };
    if (!(market.state === 'READY_B2B' || market.state === 'READY_DIRECT')) {
      return { ok: false, reason: 'market_state_' + market.state, market_key: market.market_key };
    }
    return { ok: true, market_key: market.market_key, company_location_id: ctx.company_location_id };
  }

  function freightEstimate(input) {
    input = input || {};
    if (!input.confidence || input.confidence === 'unknown') {
      return {
        amount: null,
        free: false,
        review_required: true,
        message: 'Freight requires review — unknown is not free'
      };
    }
    return {
      amount: input.amount != null ? input.amount : null,
      free: false,
      review_required: input.confidence !== 'high',
      message: input.confidence === 'high' ? 'Freight estimate available' : 'Freight confidence limited — review'
    };
  }

  function track(eventName, payload) {
    var safe = Object.assign({}, payload || {});
    delete safe.margin;
    delete safe.supplier_cost;
    delete safe.certificate_binary;
    var detail = Object.assign({ event: eventName, at: new Date().toISOString() }, safe);
    try {
      if (global.TCBAnalytics && typeof global.TCBAnalytics.track === 'function') global.TCBAnalytics.track(eventName, detail);
      global.dispatchEvent(new CustomEvent('tcb:markets', { detail: detail }));
    } catch (e) {
      /* no-op */
    }
  }

  global.TCBMarketsRuntime = {
    version: 'r39.1.0',
    maxSkus: MAX_SKUS,
    loadConfig: loadConfig,
    getMarket: getMarket,
    resolveBuyerContext: resolveBuyerContext,
    evaluatePackageSellability: evaluatePackageSellability,
    isReplenishmentAllowed: isReplenishmentAllowed,
    freightEstimate: freightEstimate,
    track: track
  };
})(typeof window !== 'undefined' ? window : globalThis);
